import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { DOCUMENT_SCROLL_ROOT_ID } from '@atlaskit/media-document-viewer/get-document-root';
import { DocumentViewer } from '@atlaskit/media-document-viewer/document-viewer';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import {
	GenericErrorBoundary,
	type ErrorComponentProps,
} from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { useMediaClient } from '@confluence/media-client/entry-points/useMediaClient.ts';

import { HtmlSheetViewer } from './HtmlSheetViewer.tsx';
import { NoticeBanner } from './NoticeBanner.tsx';
import { isExcelFile, parseViewXlsMacroParams } from './viewXlsMacroParamsUtils.ts';
import type { ViewXlsMacroParams } from './viewXlsMacroParamsUtils.ts';
import { i18n } from './viewXlsMacroI18n.ts';
import { useViewXlsData } from './hooks/useViewXlsData.ts';
import { useExcelHtmlContent } from './hooks/useExcelHtmlContent.ts';
import { useDocumentViewer } from './hooks/useDocumentViewer.ts';

export type ViewXlsMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: ViewXlsMacroParams;
};

const styles = cssMap({
	documentScrollRoot: {
		width: '100%',
		height: '100%',
		overflow: 'auto',
		position: 'relative',
	},
});

const ViewXlsMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
}: ViewXlsMacroRendererProps) => {
	const { formatMessage } = useIntl();

	const { contentId, mode, attributes, node } = macroDefaultProps;
	const extensionKey = macroDefaultProps.extensionKey ?? 'viewxls';
	const isDisplayMode = mode === 'renderer' || mode == null;
	const attributeMacroId = attributes?.macroId;
	const scrollRootId = attributeMacroId
		? `${DOCUMENT_SCROLL_ROOT_ID}-${attributeMacroId}`
		: DOCUMENT_SCROLL_ROOT_ID;
	const trackingAttributes = attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);

	const {
		attachmentName,
		pageName,
		spaceKey,
		date,
		mediaViewerWidth,
		mediaViewerHeight,
		htmlViewerWidth,
		htmlViewerHeight,
	} = parseViewXlsMacroParams(macroParams);

	const { mediaFileId, collectionName, sourceContentId, isLoading, error } = useViewXlsData({
		contentId: contentId ?? null,
		attachmentName,
		pageName,
		spaceKey,
		date,
	});

	const mediaClient = useMediaClient({
		contentId: sourceContentId ?? contentId ?? undefined,
		readOnly: true,
	});

	const isExcelAttachment = Boolean(attachmentName && isExcelFile(attachmentName));

	const {
		isViewerLoading,
		viewerError,
		viewerSucceeded,
		hasTrackedSuccess,
		thumbnailUrl,
		getContent,
		getPageImageUrl,
		onSuccess,
	} = useDocumentViewer({
		mode,
		mediaClient,
		mediaFileId,
		collectionName,
	});

	const {
		isLoading: isExcelHtmlLoading,
		htmlContent: excelHtmlContent,
		sheetNames: excelSheetNames,
		shouldShowNotice: shouldShowExcelNotice,
		error: excelError,
	} = useExcelHtmlContent({
		isDisplayMode,
		isExcelAttachment,
		mediaClient,
		mediaFileId,
		collectionName,
		attachmentName,
	});
	// --- Error guards ---

	if (!attachmentName) {
		const missingNameError = new Error(formatMessage(i18n.errorNoFilename));
		return (
			<React.Fragment>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId ?? ''}
					extensionKey={extensionKey}
					mode={mode}
					attributes={trackingAttributes}
					error={missingNameError}
				/>
				<ErrorDisplay error={missingNameError} />
			</React.Fragment>
		);
	}

	const displayError = error ?? excelError ?? viewerError;
	if (displayError) {
		return (
			<React.Fragment>
				{!hasTrackedSuccess && (
					<MacroExperienceFailure
						name={experienceName}
						contentId={contentId ?? ''}
						extensionKey={extensionKey}
						mode={mode}
						attributes={trackingAttributes}
						error={displayError}
					/>
				)}
				<ErrorDisplay error={displayError} />
			</React.Fragment>
		);
	}

	if (isLoading || !mediaClient) {
		return null;
	}

	if (!mediaFileId) {
		const missingAttachmentError = new Error(formatMessage(i18n.errorAttachmentNotFound));
		return (
			<React.Fragment>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId ?? ''}
					extensionKey={extensionKey}
					mode={mode}
					attributes={trackingAttributes}
					error={missingAttachmentError}
				/>
				<ErrorDisplay error={missingAttachmentError} />
			</React.Fragment>
		);
	}

	// Don't mount DocumentViewer while the Excel HTML artifact is loading — it will be
	// replaced by HtmlSheetViewer if the artifact succeeds, making the DocumentViewer mount wasted.
	if (isDisplayMode && isExcelAttachment && isExcelHtmlLoading) {
		return null;
	}

	if (isDisplayMode && isExcelAttachment) {
		if (excelHtmlContent && excelSheetNames.length > 0 && attachmentName) {
			return (
				<React.Fragment>
					<HtmlSheetViewer
						htmlContent={excelHtmlContent}
						sheetNames={excelSheetNames}
						width={htmlViewerWidth}
						height={htmlViewerHeight}
						attachmentName={attachmentName}
					/>
					<MacroExperienceSuccess
						name={experienceName}
						contentId={contentId ?? ''}
						extensionKey={extensionKey}
						mode={mode}
						attributes={trackingAttributes}
					/>
				</React.Fragment>
			);
		}
	}

	// In editor mode, show thumbnail if available, otherwise render nothing —
	// DocumentViewer is expensive and should not mount in editor mode.
	if (mode === 'editor') {
		if (thumbnailUrl) {
			return (
				<React.Fragment>
					<MacroExperienceSuccess
						name={experienceName}
						contentId={contentId ?? ''}
						extensionKey={extensionKey}
						mode={mode}
						attributes={trackingAttributes}
					/>
					<img
						src={thumbnailUrl}
						alt={formatMessage(i18n.thumbnailAlt, { filename: attachmentName })}
						data-testid="viewxls-macro-thumbnail"
					/>
				</React.Fragment>
			);
		}
		return null;
	}

	if (isDisplayMode && !attributeMacroId) {
		return null;
	}

	// Display mode: render DocumentViewer (PDF preview for all office file types)
	return (
		<React.Fragment>
			{isDisplayMode && shouldShowExcelNotice && attachmentName && (
				<NoticeBanner
					fileName={attachmentName}
					attachmentPageUrl={`/wiki/pages/viewpageattachments.action?pageId=${sourceContentId ?? contentId ?? ''}`}
					iconUrl="/wiki/s/-/images/icons/excel.png"
				/>
			)}
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic values from macro params cannot be expressed as static CSS
				style={{ width: mediaViewerWidth, height: mediaViewerHeight, position: 'relative' }}
				data-testid="viewxls-macro-viewer"
				data-fabric-macro={attributes?.macroId}
			>
				<div
					id={scrollRootId}
					data-testid="viewxls-macro-scroll-root"
					style={{ opacity: isViewerLoading ? 0 : 1 }}
					css={styles.documentScrollRoot}
				>
					<DocumentViewer
						getContent={getContent}
						getPageImageUrl={getPageImageUrl}
						zoom={1}
						onSuccess={onSuccess}
					/>
				</div>
				{viewerSucceeded && (
					<MacroExperienceSuccess
						name={experienceName}
						contentId={contentId ?? ''}
						extensionKey={extensionKey}
						mode={mode}
						attributes={trackingAttributes}
					/>
				)}
			</div>
		</React.Fragment>
	);
};

export const ViewXlsMacroRenderer = (props: ViewXlsMacroRendererProps): React.JSX.Element => {
	const { macroDefaultProps } = props;
	const { contentId, mode, attributes, node } = macroDefaultProps;
	const extensionKey = macroDefaultProps.extensionKey ?? 'viewxls';
	const trackingAttributes = attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);

	const renderErrorFallback = ({ error }: ErrorComponentProps) => (
		<React.Fragment>
			<MacroExperienceFailure
				name={experienceName}
				contentId={contentId ?? ''}
				extensionKey={extensionKey}
				mode={mode}
				attributes={trackingAttributes}
				error={error}
			/>
			<FormattedMessage {...i18n.errorGeneric} />
		</React.Fragment>
	);

	return (
		<GenericErrorBoundary
			renderOnError={renderErrorFallback}
			attribution={Attribution.PAGE_EXPERIENCES}
		>
			<ViewXlsMacroRendererComponent {...props} />
		</GenericErrorBoundary>
	);
};
